import { useCallback, useState } from 'react'

// The intrinsic (decoded) pixel size the player reports for a clip.
export interface VideoDimensionsI {
  width: number
  height: number
}

// Decoded dimensions tagged with the clip they were captured from, so a source
// switch invalidates them.
interface SourcedVideoFrameI extends VideoDimensionsI {
  sourceKey: string | null
}

interface UseVideoOverlayDimensionsIProps {
  // The camera's configured stream resolution — the grid's coordinate space.
  configuredWidth?: number | null
  configuredHeight?: number | null
  // Identifies the clip currently loaded (its URL). Decoded dimensions are tied
  // to this key, so when the source changes the previous clip's frame is ignored
  // immediately instead of lingering as a stale viewBox until the next onReady.
  sourceKey?: string | null
}

interface UseVideoOverlayDimensionsResultIProps {
  viewWidth: number | null
  viewHeight: number | null
  onVideoDimensions: (dimensions: VideoDimensionsI) => void
  // The camera's configured resolution (the grid's coordinate space) and the
  // clip's decoded resolution. ScalableGridOverlay scales the grid from the
  // former into the latter, per-axis — mirroring the evaluator.
  cameraDimensions: VideoDimensionsI | null
  clipDimensions: VideoDimensionsI | null
}

const toDimensions = (
  width?: number | null,
  height?: number | null
): VideoDimensionsI | null =>
  width && height && width > 0 && height > 0 ? { width, height } : null

/**
 * Resolves the viewBox dimensions for a grid overlay drawn over a VideoPlayer,
 * and returns the `onVideoDimensions` handler to wire into that player.
 *
 * The grid polygons are authored in the camera's configured resolution, but the
 * evaluator scales them into the analysed clip's pixel space per-axis before
 * computing its verdict (`scale_candidate_grid` / the production
 * `scale_camera_configuration_grid_resolution`, both `TailgateGridTestJob`
 * `scalingMode: STORED_RESOLUTION`). To show the operator the same grid the
 * verdict used, the overlay's viewBox is the decoded clip frame and the points
 * are scaled the same way — which also letterboxes identically to the <video>
 * (object-fit: contain is the browser default for video). The only case the
 * evaluator declines to scale is `scalingMode: NONE` (no decoded resolution);
 * the overlay mirrors that by drawing nothing until a frame is known.
 *
 * Decoded dimensions are associated with `sourceKey`, so a clip switch never
 * carries the previous clip's frame into the new one.
 */
export const useVideoOverlayDimensions = ({
  configuredWidth,
  configuredHeight,
  sourceKey,
}: UseVideoOverlayDimensionsIProps): UseVideoOverlayDimensionsResultIProps => {
  const currentSourceKey = sourceKey ?? null

  // Decoded dimensions the player reports once metadata loads, tagged with the
  // clip they belong to.
  const [frame, setFrame] = useState<SourcedVideoFrameI | null>(null)

  const onVideoDimensions = useCallback(
    ({ width, height }: VideoDimensionsI) =>
      setFrame({ sourceKey: currentSourceKey, width, height }),
    [currentSourceKey]
  )

  const cameraDimensions = toDimensions(configuredWidth, configuredHeight)
  // Ignore dimensions captured for a different clip — the source has changed and
  // the old frame no longer describes what is on screen.
  const clipDimensions =
    frame && frame.sourceKey === currentSourceKey
      ? toDimensions(frame.width, frame.height)
      : null

  // The viewBox is the frame the video actually decodes; fall back to the
  // configured resolution before the clip loads.
  const view = clipDimensions ?? cameraDimensions

  return {
    viewWidth: view?.width ?? null,
    viewHeight: view?.height ?? null,
    onVideoDimensions,
    cameraDimensions,
    clipDimensions,
  }
}
